import { AsyncPipe, NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { distinctUntilChanged, filter, map, merge, of, scan, shareReplay, startWith } from 'rxjs';
import { HeaderComponent } from './core/layout/header/header.component';
import { FooterComponent } from './core/layout/footer/footer.component';
import { EnvBannerComponent } from './core/layout/env-banner/env-banner.component';
import { PageLoadingComponent } from './shared/components/page-loading/page-loading.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [EnvBannerComponent, RouterOutlet, HeaderComponent, FooterComponent, NgIf, AsyncPipe, PageLoadingComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private router = inject(Router);
  title = 'boloarena';

  readonly routeLoading$ = merge(
    this.router.events.pipe(
      map(event => {
        if (event instanceof RouteConfigLoadStart) return 1;
        if (event instanceof RouteConfigLoadEnd) return -1;
        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          return Number.NEGATIVE_INFINITY;
        }
        return 0;
      })
    ),
    of(0)
  ).pipe(
    scan((pending, delta) => {
      if (delta === Number.NEGATIVE_INFINITY) {
        return 0;
      }
      return Math.max(0, pending + delta);
    }, 0),
    map(pending => pending > 0),
    distinctUntilChanged()
  );

  readonly loadingTargetUrl$ = this.router.events.pipe(
    filter((event): event is NavigationStart => event instanceof NavigationStart),
    map(event => event.url),
    startWith(this.router.url),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly loadingTitle$ = this.loadingTargetUrl$.pipe(
    map(url => this.isLandingUrl(url) ? 'Loading BOLOARENA' : 'Opening your page')
  );

  readonly loadingMessage$ = this.loadingTargetUrl$.pipe(
    map(url =>
      this.isLandingUrl(url)
        ? 'Bringing in the homepage, locations and latest highlights.'
        : 'We are loading the route and setting everything up.'
    )
  );

  private isLandingUrl(url: string | null | undefined): boolean {
    if (!url) return true;
    const normalizedUrl = url.split('?')[0].split('#')[0];
    return normalizedUrl === '' || normalizedUrl === '/';
  }
}
